import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    showImages = "showImages",
    showOnlyTopLevelCategories = "showOnlyTopLevelCategories",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImages]: boolean;
        [fields.showOnlyTopLevelCategories]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const getCategoriesParameter = {
        maxDepth: props.fields.showOnlyTopLevelCategories ? 1 : 2,
        includeStartCategory: false,
    };
    return {
        categoriesDataView: getCategoriesDataView(state, getCategoriesParameter),
        getCategoriesParameter,
        topLevelCategoryIds: getSubCategoryIds(state, emptyGuid),
    };
};

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface HomePageCategoriesStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: GridContainerProps;
    categoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    categoryImageItem?: GridItemProps;
    categoryImageLink?: LinkPresentationProps;
    categoryImage?: LazyImageProps;
    categoryNameLinkItem?: GridItemProps;
    categoryNameLink?: LinkPresentationProps;
    categoryName?: TypographyProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    viewMoreLinkItem?: LinkPresentationProps;
    mainWrapper?: InjectableCss;
}

export const homePageCategoriesStyles: HomePageCategoriesStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    mainWrapper: {
        css: css`
            text-align: center;
        `,
    },
    container: {
        css: css`
            margin-top: 20px;
            padding-bottom: 36px !important;
            max-width: 100% !important;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 0 1rem;
                        `,
                        null,
                        null,
                        css`
                            padding: 0 5rem;
                        `,
                        null,
                    ],
                    "min",
                );
            }}
            > div {
                width: auto !important;
                margin: 0;
                justify-content: center;
                > div {
                    padding: 0;
                    margin: 10px !important;
                    > div {
                        > div {
                            width: 100% !important;
                            > div:first-child {
                                padding: 0;
                            }
                        }
                    }
                }
            }
        `,
    },
    categoryItem: {
        // width is dynamic here
        css: css`
            max-width: none;
            [class^="StyledButton"] {
                width: 100%;
                overflow: hidden;
                > [class^="TypographyStyle"] {
                    width: 100%;
                    > [class^="LazyImageStyle"] {
                        width: 100%;
                    }
                }
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-basis: calc(100% - 20px);
                        `,
                        css`
                            flex-basis: calc(50% - 20px);
                        `,
                        css`
                            flex-basis: calc(33.33% - 20px);
                        `,
                        css`
                            flex-basis: calc(20% - 20px);
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    innerContainer: {
        gap: 10,
        offsetCss: css`
            margin: 0;
        `,
    },
    categoryImageItem: {
        width: 12,
    },
    categoryImage: {
        width: "160px",
        height: "100%",
        css: css`
            border-radius: 12px;
            overflow: hidden;
            > img {
                transition: transform 0.5s;
                &:hover {
                    transform: scale(1.2);
                }
            }
        `,
    },
    categoryNameLinkItem: {
        width: 12,
        css: css`
            padding-bottom: 8px;
        `,
    },
    categoryName: {
        css: css`
            display: block;
            text-align: center;
            color: ${getColor("text.accent")};
            font-size: 20px;
            font-weight: 500;
            margin-top: 12px;
        `,
    },
    subCategoryNameLinkItem: {
        width: 12,
    },
    viewMoreLinkItem: {
        css: css`
            margin-bottom: 36px;
            font-weight: bold;
            margin-top: -10px;
        `,
    },
};

const styles = homePageCategoriesStyles;

class HomePageCategories extends React.Component<Props> {
    container = React.createRef<HTMLDivElement>();

    UNSAFE_componentWillMount(): void {
        const { categoriesDataView, getCategoriesParameter, loadCategories } = this.props;
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories(getCategoriesParameter);
        }
    }

    render() {
        const {
            categoriesDataView,
            topLevelCategoryIds,
            fields: { showImages, showOnlyTopLevelCategories },
        } = this.props;
        if (categoriesDataView.isLoading) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (!categoriesDataView.value || !topLevelCategoryIds) {
            return null;
        }

        const categories = categoriesDataView.value.filter(o => topLevelCategoryIds.indexOf(o.id) > -1);
        const categoriesToShow = categories.slice(0, 5);
        if (categories.length === 0) {
            return null;
        }

        return (
            <StyledWrapper ref={this.container} {...styles.mainWrapper}>
                <GridContainer {...styles.container}>
                    {categoriesToShow.map(category => (
                        <GridItem key={category.id.toString()} {...styles.categoryItem}>
                            <GridContainer {...styles.innerContainer}>
                                {showImages && (
                                    <GridItem {...styles.categoryImageItem}>
                                        {category.path && (
                                            <Link href={category.path} {...styles.categoryImageLink}>
                                                <LazyImage
                                                    src={category.smallImagePath}
                                                    {...styles.categoryImage}
                                                    altText={category.imageAltText}
                                                />
                                            </Link>
                                        )}
                                    </GridItem>
                                )}
                                <GridItem {...styles.categoryNameLinkItem}>
                                    <Link href={category.path} {...styles.categoryNameLink}>
                                        <Typography {...styles.categoryName}>{category.shortDescription}</Typography>
                                    </Link>
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                    ))}
                </GridContainer>
                {categories.length >= 5 && (
                    <Link href="/Categories" {...styles.viewMoreLinkItem}>
                        <Button variant="secondary">View More</Button>
                    </Link>
                )}
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(HomePageCategories)),
    definition: {
        group: "Categories",
        icon: "list-ul",
        allowedContexts: ["HomePage"],
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
